import React, { useCallback, useMemo } from 'react'
import { motion } from 'framer-motion'
import { When } from 'react-if'
import { Button } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { HeadingLG, ParagraphMD, TitleMD } from '@/atoms/Text'
import { ExperimentVariation, GBFadeInExperiment } from '@/experimentation'
import { TheatricalReleaseObject } from '@/types/codegen-federation'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { TheatricalPifExpressOffers } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifExpressOffers'
import { TheatricalPifCtaSection } from '@/views/PayItForward/TheatricalPayItForwardView/sections/TheatricalPifCtaSection'
import { TheatricalPifShareSection } from '@/views/PayItForward/TheatricalPayItForwardView/sections/TheatricalPifShareSection'
import ExternalCta from '@/views/TicketCheckoutViews/components/ExternalCta'

export interface TheatricalPifHowYouCanHelpSectionProps {
  theatricalRelease: TheatricalReleaseObject
  shouldShowPifSection: boolean
  setShouldShowPifSection: React.Dispatch<React.SetStateAction<boolean>>
}

const variants = {
  open: { opacity: 1 },
  closed: { opacity: 0, display: 'none' },
}

export const TheatricalPifHowYouCanHelpSection: React.FC<TheatricalPifHowYouCanHelpSectionProps> = ({
  theatricalRelease,
  shouldShowPifSection,
  setShouldShowPifSection,
}) => {
  const { query, asPath } = useSafeRouter()

  const { t } = useTranslate('pif')
  const togglePifSection = useCallback(() => {
    setShouldShowPifSection((state) => !state)
  }, [setShouldShowPifSection])

  const canShowOffersForOfferLink = useMemo(() => {
    return Boolean(Array.isArray(query.offer) || query.offer)
  }, [query.offer])

  return (
    <>
      <div className="mb-6 flex items-center justify-between">
        <HeadingLG
          weight="bold"
          color="core-gray-950"
          className="text-2xl tracking-tight lg:text-[1.35rem] xl:text-2xl"
        >
          {t('howYouCanHelp', 'How You Can Help')}
        </HeadingLG>
        <When condition={shouldShowPifSection}>
          <Button
            className="text-base font-medium text-oxide"
            onClick={() => {
              setShouldShowPifSection(false)
            }}
            variant="plain"
          >
            {t('viewAll', 'View All')}
          </Button>
        </When>
      </div>
      <GBFadeInExperiment experimentName="careportal-pif-page-location" defaultValue={false}>
        <ExperimentVariation variation={true}>
          <div>{theatricalRelease?.externalCta && <ExternalCta externalCta={theatricalRelease.externalCta} />}</div>
        </ExperimentVariation>
        <ExperimentVariation variation={false}>
          <div></div>
        </ExperimentVariation>
      </GBFadeInExperiment>

      <div
        className={`${
          !shouldShowPifSection && 'my-4 rounded-2xl border border-solid border-gray-200 bg-white px-4 py-6 md:py-10'
        }`}
      >
        <FlexColumn className="relative gap-2">
          <TitleMD weight="bold" className={`${!shouldShowPifSection && 'text-center'}`}>
            {t('payItForwardv2', 'Pay it Forward')}
          </TitleMD>
          <h4 aria-hidden="true" className="photon-title-md h-0 font-bold opacity-0">
            {t('payItForwardv2', 'Pay it Forward')}
          </h4>
          <ParagraphMD
            className={`${!shouldShowPifSection && 'text-center md:mx-auto md:max-w-xs'}`}
            color="core-gray-700"
          >
            {t(
              'provideFreeTicketsForSomeone',
              'Provide free tickets for someone to claim and watch at the movie theaters.',
            )}
          </ParagraphMD>
        </FlexColumn>
        <motion.div animate={canShowOffersForOfferLink || shouldShowPifSection ? 'open' : 'closed'} variants={variants}>
          <div className="mt-4">
            <TheatricalPifExpressOffers
              theatricalRelease={theatricalRelease}
              isOneTimeOnly
              shouldShowDedicationExperiment={canShowOffersForOfferLink}
            />
          </div>
        </motion.div>
        <motion.div animate={shouldShowPifSection ? 'closed' : 'open'} variants={variants}>
          <TheatricalPifCtaSection theatricalRelease={theatricalRelease} togglePifSection={togglePifSection} />
        </motion.div>
      </div>
      <GBFadeInExperiment experimentName="careportal-pif-page-location" defaultValue={false}>
        <ExperimentVariation variation={true}>
          <div></div>
        </ExperimentVariation>
        <ExperimentVariation variation={false}>
          <div>{theatricalRelease?.externalCta && <ExternalCta externalCta={theatricalRelease.externalCta} />}</div>
        </ExperimentVariation>
      </GBFadeInExperiment>
      <div className="my-4 mt-8 rounded-2xl border border-solid border-gray-200 bg-white px-4 py-6 md:py-10">
        <TheatricalPifShareSection
          theatricalSlug={theatricalRelease?.theatricalSlug}
          projectName={theatricalRelease?.title as string}
          showYourImpact
          shareUrl={asPath}
        />
      </div>
    </>
  )
}
